import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement|HTMLSelectElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class CustomQuantitySelectorComponent extends Component {
  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const { quantityInput } = this.refs;
    if (!quantityInput) return;

    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.stepUp();
    } else if (quantityInput instanceof HTMLSelectElement) {
      const currentIndex = quantityInput.selectedIndex;
      if (currentIndex < quantityInput.options.length - 1) {
        quantityInput.selectedIndex = currentIndex + 1;
      }
    }

    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const { quantityInput } = this.refs;
    if (!quantityInput) return;

    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.stepDown();
    } else if (quantityInput instanceof HTMLSelectElement) {
      const currentIndex = quantityInput.selectedIndex;
      if (currentIndex > 0) {
        quantityInput.selectedIndex = currentIndex - 1;
      }
    }

    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (quantityInput instanceof HTMLInputElement && document.activeElement === quantityInput) {
      quantityInput.select();
    }
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const { quantityInput } = this.refs;
    if (!quantityInput) return;

    quantityInput.value = event.target.value;
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;
    if (!quantityInput) return;

    this.#checkQuantityRules();

    const newValue = parseInt(quantityInput.value, 10);
    quantityInput.dispatchEvent(
      new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine))
    );
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    if (!quantityInput) return;

    if (quantityInput instanceof HTMLInputElement) {
      const { min, max, value: newValue } = quantityInput;
      if (min && newValue < min) quantityInput.value = min;
      if (max && newValue > max) quantityInput.value = max;
    }
  };

  /**
   * Gets the quantity input or select.
   */
  get quantityInput() {
    const el = this.refs.quantityInput;
    if (!el || !(el instanceof HTMLInputElement || el instanceof HTMLSelectElement)) {
      throw new Error('Missing <input> or <select ref="quantityInput" /> inside <quantity-selector-component />');
    }
    return el;
  }

  /**
   * Wait for refs and bind events once available
   */
  connectedCallback() {
    super.connectedCallback?.();

    const bindChangeListener = () => {
      const { quantityInput } = this.refs;
      if (quantityInput) {
        quantityInput.addEventListener('change', () => this.#onQuantityChange());
        return true;
      }
      return false;
    };

    // Intento inmediato
    if (!bindChangeListener()) {
      // Si aún no está disponible, observa hasta que aparezca
      const observer = new MutationObserver(() => {
        if (bindChangeListener()) observer.disconnect();
      });
      observer.observe(this, { childList: true, subtree: true });
    }
  }
}

if (!customElements.get('custom-quantity-selector-component')) {
  customElements.define('custom-quantity-selector-component', CustomQuantitySelectorComponent);
}
